import React, { useState, useEffect } from "react";
import axiosInstance from "./axiosInstance";
import { Button } from "./ui-common/button";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui-common/tooltip";

function Feedback({
  conversation_id,
  message_id,
  conversation_course,
  existingVote,
  existingReason = "",
  readOnly = false,
  onVoteUpdate,
}) {
  const [vote, setVote] = useState(existingVote || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [pendingVote, setPendingVote] = useState(""); // Track which vote triggered the form

  // Update vote state when existingVote prop changes (e.g., switching conversations)
  useEffect(() => {
    setVote(existingVote || "");
    setShowFeedbackForm(false);
    setFeedbackText("");
    setPendingVote("");
    setSubmitted(false);
  }, [existingVote, message_id]);

  const handleVote = async (e) => {
    const newVote = e.currentTarget.value;

    // If clicking the same vote again, toggle off
    if (newVote === vote && !showFeedbackForm) {
      setVote("");
      setShowFeedbackForm(false);
      setFeedbackText("");
      setPendingVote("");
      return;
    }

    // Show feedback form for both positive and negative votes
    setPendingVote(newVote);
    setVote(newVote);
    setShowFeedbackForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post("/api/feedback", {
        conversation_id,
        message_id,
        conversation_course,
        vote: pendingVote,
        feedback: feedbackText,
      });

      // Update the chatHistory with the new vote
      if (onVoteUpdate) {
        onVoteUpdate(message_id, { vote: pendingVote });
      }

      setSubmitted(true);
      setShowFeedbackForm(false);
      setFeedbackText("");
      setPendingVote("");
      setIsSubmitting(false);

      setTimeout(() => {
        setSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred while processing the request.");
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowFeedbackForm(false);
    setFeedbackText("");
    setVote("");
    setPendingVote("");
  };

  return (
    <div className="flex items-center gap-1">
      {readOnly ? (
        <>
          <div className="flex items-center gap-1">
            {existingVote === "positive" && (
              <ThumbsUp className="h-4 w-4 text-green-500" />
            )}
            {existingVote === "negative" && (
              <ThumbsDown className="h-4 w-4 text-destructive" />
            )}
          </div>
          {existingVote === "negative" && !!existingReason && (
            <p className="text-xs text-muted-foreground">
              Reason: <span className="italic">{existingReason}</span>
            </p>
          )}
        </>
      ) : (
        <>
          <div className="feedback-wrapper">
            {/* Feedback form (shown for both positive and negative votes) */}
            {showFeedbackForm && (
              <form onSubmit={onSubmit} className="feedback-explain">
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={
                    pendingVote === "positive"
                      ? "What did you like about this response?"
                      : "How could this response be improved?"
                  }
                  className="feedback-textarea"
                />

                <div className="feedback-actions">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={isSubmitting}
                    className="h-8 px-3 text-xs"
                  >
                    Submit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {submitted && (
              <div className="feedback-explain">
                <div className="feedback-explain-title">Thanks for your feedback! 🎉</div>
                <div className="text-xs text-muted-foreground">
                  Submitted successfully
                </div>
              </div>
            )}

            {/* Positive button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  value="positive"
                  onClick={handleVote}
                  className={`h-8 w-8 p-0 m-0 transition-all duration-200 border-2 ${
                    vote === "positive"
                      ? "text-green-500 border-green-500 bg-green-50 dark:bg-green-950/30 hover:bg-green-100 dark:hover:bg-green-950/50"
                      : "text-green-600 dark:text-green-400 border-green-300 dark:border-green-700 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-950/30 hover:scale-110"
                  }`}
                  disabled={isSubmitting}
                >
                  <ThumbsUp className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Good response</p>
              </TooltipContent>
            </Tooltip>

            {/* Negative button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  value="negative"
                  onClick={handleVote}
                  className={`h-8 w-8 p-0 m-0 transition-all duration-200 border-2 ${
                    vote === "negative"
                      ? "text-red-500 border-red-500 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50"
                      : "text-red-500 dark:text-red-400 border-red-300 dark:border-red-700 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 hover:scale-110"
                  }`}
                  disabled={isSubmitting}
                >
                  <ThumbsDown className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Needs improvement</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </>
      )}
    </div>
  );
}

export default Feedback;
